import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Search,
  Package,
  Wrench,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Plus,
  Pencil,
  Archive,
  Loader2,
  AlertCircle,
  Filter,
  History,
} from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import {
  GET_PRODUCTS,
  CREATE_PRODUCT,
  UPDATE_PRODUCT,
  ARCHIVE_PRODUCT,
  type Product,
  type ProductCategory,
  type GetProductsData,
  type GetProductsVariables,
  type CreateProductData,
  type UpdateProductData,
  type ArchiveProductData,
  type PriceHistoryEntry,
} from '@/graphql/products';

const CATEGORY_LABELS: Record<ProductCategory, string> = {
  SOFTWARE: 'Software',
  SERVICES: 'Services',
};

const CATEGORY_ICONS: Record<ProductCategory, typeof Package> = {
  SOFTWARE: Package,
  SERVICES: Wrench,
};

const CATEGORY_STYLES: Record<ProductCategory, string> = {
  SOFTWARE: 'bg-pond/10 text-pond dark:bg-pond-light/20 dark:text-pond-light',
  SERVICES: 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua',
};

const ITEMS_PER_PAGE = 10;

// Helper to format number with commas
function formatPriceInput(value: string): string {
  // Remove commas for parsing
  const numericValue = value.replace(/,/g, '');
  const num = parseFloat(numericValue);
  if (isNaN(num)) return value;
  
  // Format with commas and 2 decimal places
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Helper to parse price input (strip commas)
function parsePriceValue(value: string): number {
  return parseFloat(value.replace(/,/g, '')) || 0;
}

export function Products() {
  const { canWrite } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ProductCategory | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState<string | null>(null);
  const [showPriceHistory, setShowPriceHistory] = useState<Product | null>(null);

  // Form state
  const [formSku, setFormSku] = useState('');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formCategory, setFormCategory] = useState<ProductCategory>('SOFTWARE');
  const [formIsActive, setFormIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // GraphQL Query
  const { data, loading, error, refetch } = useQuery<GetProductsData, GetProductsVariables>(
    GET_PRODUCTS,
    {
      variables: {
        search: searchQuery || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  // GraphQL Mutations
  const [createProduct] = useMutation<CreateProductData>(CREATE_PRODUCT);
  const [updateProduct] = useMutation<UpdateProductData>(UPDATE_PRODUCT);
  const [archiveProduct] = useMutation<ArchiveProductData>(ARCHIVE_PRODUCT);

  const products = useMemo(() => data?.products.items || [], [data?.products.items]);
  const pageInfo = data?.products.pageInfo;
  const totalPages = pageInfo?.totalPages || 1;

  // Summary stats
  const softwareCount = products.filter(p => p.category === 'SOFTWARE').length;
  const servicesCount = products.filter(p => p.category === 'SERVICES').length;
  
  // Most used products (by price history count as proxy for activity)
  const mostActiveSoftware = useMemo(() => 
    [...products.filter(p => p.category === 'SOFTWARE')]
      .sort((a, b) => (b.priceHistory?.length || 0) - (a.priceHistory?.length || 0))[0],
    [products]
  );
  const mostActiveServices = useMemo(() => 
    [...products.filter(p => p.category === 'SERVICES')]
      .sort((a, b) => (b.priceHistory?.length || 0) - (a.priceHistory?.length || 0))[0],
    [products]
  );

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: ProductCategory | 'all') => {
    setCategoryFilter(value);
    setCurrentPage(1);
  };

  const resetForm = () => {
    setFormSku('');
    setFormName('');
    setFormDescription('');
    setFormPrice('');
    setFormCategory('SOFTWARE');
    setFormIsActive(true);
  };

  const openEditModal = (product: Product) => {
    setFormSku(product.sku);
    setFormName(product.name);
    setFormDescription(product.description || '');
    setFormPrice(formatPriceInput(product.unitPrice.toString()));
    setFormCategory(product.category);
    setFormIsActive(product.isActive);
    setEditingProduct(product);
  };

  const handleSave = async () => {
    if (!formSku || !formName) return;

    setIsSaving(true);
    try {
      if (editingProduct) {
        await updateProduct({
          variables: {
            id: editingProduct.id,
            input: {
              sku: formSku,
              name: formName,
              description: formDescription || undefined,
              unitPrice: parsePriceValue(formPrice),
              category: formCategory,
              isActive: formIsActive,
            },
          },
        });
      } else {
        await createProduct({
          variables: {
            input: {
              sku: formSku,
              name: formName,
              description: formDescription || undefined,
              unitPrice: parsePriceValue(formPrice),
              category: formCategory,
              isActive: formIsActive,
            },
          },
        });
      }
      setShowAddModal(false);
      setEditingProduct(null);
      resetForm();
      refetch();
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save product. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archiveProduct({ variables: { id } });
      setShowArchiveConfirm(null);
      refetch();
    } catch (err) {
      console.error('Archive failed:', err);
      alert('Failed to archive product. Please try again.');
    }
  };

  if (error) {
    return (
      <div className="p-8 text-center text-rust">
        <AlertCircle className="w-12 h-12 mx-auto mb-3" />
        <p>Error loading products: {error.message}</p>
        <button onClick={() => refetch()} className="btn-secondary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate dark:text-mist">Products</h1>
          <p className="text-sm text-chalkboard dark:text-rain-cloud mt-1">
            Manage software and service product catalog
          </p>
        </div>
        {canWrite('products') && (
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Software Total */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
                <Package className="w-5 h-5 text-pond dark:text-pond-light" />
              </div>
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Software Products</p>
                <p className="text-2xl font-semibold text-slate dark:text-mist">
                  {softwareCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Most Active Software */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet/10 dark:bg-violet-light/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-violet dark:text-violet-light" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Top Software</p>
                <p className="text-sm font-semibold text-slate dark:text-mist truncate">
                  {mostActiveSoftware?.name || '-'}
                </p>
                <p className="text-xs text-chalkboard dark:text-rain-cloud">
                  {mostActiveSoftware ? formatCurrency(mostActiveSoftware.unitPrice) : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Total */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-turquoise/10 dark:bg-aqua/20 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-turquoise dark:text-aqua" />
              </div>
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Service Products</p>
                <p className="text-2xl font-semibold text-slate dark:text-mist">
                  {servicesCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Most Active Services */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange/10 dark:bg-orange/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Top Service</p>
                <p className="text-sm font-semibold text-slate dark:text-mist truncate">
                  {mostActiveServices?.name || '-'}
                </p>
                <p className="text-xs text-chalkboard dark:text-rain-cloud">
                  {mostActiveServices ? formatCurrency(mostActiveServices.unitPrice) : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
        {/* Search */}
        <div className="relative" style={{ flex: '1 1 auto', minWidth: 0 }}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chalkboard dark:text-rain-cloud pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by SKU, name, or description..."
            className="input"
            style={{ width: '100%', paddingLeft: '2.5rem' }}
          />
        </div>
        
        {/* Category Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '0 0 auto' }}>
          <Filter className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
          <select
            value={categoryFilter}
            onChange={(e) => handleCategoryChange(e.target.value as ProductCategory | 'all')}
            className="select"
            style={{ width: '10rem' }}
          >
            <option value="all">All Categories</option>
            <option value="SOFTWARE">Software</option>
            <option value="SERVICES">Services</option>
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-chalkboard dark:text-rain-cloud">
          Showing {products.length} of {pageInfo?.totalCount || 0} products
          {categoryFilter !== 'all' && ` in ${CATEGORY_LABELS[categoryFilter]}`}
          {searchQuery && ` matching "${searchQuery}"`}
        </p>
      </div>

      {/* Products Table */}
      <div className="card overflow-hidden">
        {loading && products.length === 0 ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-pond" />
            <p className="text-chalkboard dark:text-rain-cloud">Loading products...</p>
          </div>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product Name</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th className="text-right">Unit Price</th>
                  <th className="text-center">Status</th>
                  {canWrite('products') && <th className="w-28">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const CategoryIcon = CATEGORY_ICONS[product.category];
                  const hasPriceHistory = product.priceHistory && product.priceHistory.length > 0;
                  return (
                    <tr key={product.id} className="group">
                      <td>
                        <span className="font-mono text-xs text-chalkboard dark:text-rain-cloud">
                          {product.sku}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center',
                            CATEGORY_STYLES[product.category]
                          )}>
                            <CategoryIcon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium text-slate dark:text-mist">{product.name}</p>
                            {!product.isActive && (
                              <span className="badge-neutral text-xs">Inactive</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="max-w-xs">
                        <p className="text-sm text-chalkboard dark:text-rain-cloud truncate">
                          {product.description || '-'}
                        </p>
                      </td>
                      <td>
                        <span className={cn('badge', CATEGORY_STYLES[product.category])}>
                          {CATEGORY_LABELS[product.category]}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="font-medium text-slate dark:text-mist">
                            {formatCurrency(product.unitPrice)}
                          </span>
                          {hasPriceHistory && (
                            <button
                              onClick={() => setShowPriceHistory(product)}
                              className="btn-ghost btn-icon btn-xs text-chalkboard dark:text-rain-cloud"
                              title="View price history"
                            >
                              <History className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        <span className={cn(
                          'badge',
                          product.isActive 
                            ? 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua'
                            : 'badge-neutral'
                        )}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {canWrite('products') && (
                        <td>
                          <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => openEditModal(product)}
                              className="btn-ghost btn-icon btn-sm"
                              title="Edit product"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setShowArchiveConfirm(product.id)}
                              className="btn-ghost btn-icon btn-sm text-orange dark:text-orange-light"
                              title="Archive product"
                            >
                              <Archive className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {products.length === 0 && (
              <div className="p-8 text-center text-chalkboard dark:text-rain-cloud">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No products found matching your search criteria.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="btn-secondary btn-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={cn(
                      'w-8 h-8 rounded-lg text-sm font-medium transition-colors',
                      currentPage === pageNum
                        ? 'bg-pond text-white dark:bg-pond-light'
                        : 'text-chalkboard dark:text-rain-cloud hover:bg-pale-gray-2 dark:hover:bg-night-pond'
                    )}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="btn-secondary btn-sm"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      <Modal
        isOpen={showAddModal || !!editingProduct}
        onClose={() => { setShowAddModal(false); setEditingProduct(null); resetForm(); }}
        title={editingProduct ? 'Edit Product' : 'Add Product'}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                SKU Code
              </label>
              <input
                type="text"
                value={formSku}
                onChange={(e) => setFormSku(e.target.value)}
                placeholder="PROD-XXX-001"
                className="input font-mono"
                disabled={isSaving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Category
              </label>
              <select
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value as ProductCategory)}
                className="select"
                disabled={isSaving}
              >
                <option value="SOFTWARE">Software</option>
                <option value="SERVICES">Services</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Product Name
            </label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Enter product name"
              className="input"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Description
            </label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Enter product description"
              rows={3}
              className="input resize-none"
              disabled={isSaving}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Unit Price
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-chalkboard dark:text-rain-cloud pointer-events-none">$</span>
                <input
                  type="text"
                  value={formPrice}
                  onChange={(e) => {
                    // Allow only numbers, decimal point, and commas
                    const value = e.target.value.replace(/[^0-9.,]/g, '');
                    // Remove commas for validation
                    const numericValue = value.replace(/,/g, '');
                    // Prevent multiple decimals
                    const parts = numericValue.split('.');
                    if (parts.length > 2) return;
                    // Limit to 2 decimal places
                    if (parts[1] && parts[1].length > 2) return;
                    setFormPrice(numericValue);
                  }}
                  onBlur={() => {
                    // Format with commas and 2 decimal places on blur
                    if (formPrice) {
                      setFormPrice(formatPriceInput(formPrice));
                    }
                  }}
                  placeholder="0.00"
                  className="input"
                  style={{ paddingLeft: '1.5rem' }}
                  disabled={isSaving}
                />
              </div>
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formIsActive}
                  onChange={(e) => setFormIsActive(e.target.checked)}
                  className="rounded border-gray dark:border-night-pond text-pond focus:ring-pond"
                  disabled={isSaving}
                />
                <span className="text-sm text-slate dark:text-mist">Active product</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setShowAddModal(false); setEditingProduct(null); resetForm(); }}
              className="btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="btn-primary"
              disabled={!formName || !formSku || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingProduct ? 'Save Changes' : 'Add Product'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal
        isOpen={!!showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(null)}
        title="Archive Product"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            Are you sure you want to archive this product? You can restore it later.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowArchiveConfirm(null)} className="btn-secondary">
              Cancel
            </button>
            <button 
              onClick={() => showArchiveConfirm && handleArchive(showArchiveConfirm)}
              className="btn-primary bg-orange hover:bg-orange/90"
            >
              <Archive className="w-4 h-4" />
              Archive
            </button>
          </div>
        </div>
      </Modal>

      {/* Price History Modal */}
      <Modal
        isOpen={!!showPriceHistory}
        onClose={() => setShowPriceHistory(null)}
        title={`Price History - ${showPriceHistory?.name || ''}`}
      >
        <div className="space-y-4">
          {/* Current Price */}
          <div className="p-3 bg-turquoise/10 dark:bg-aqua/10 rounded-lg">
            <p className="text-xs text-chalkboard dark:text-rain-cloud mb-1">Current Price</p>
            <p className="text-xl font-semibold text-slate dark:text-mist">
              {showPriceHistory && formatCurrency(showPriceHistory.unitPrice)}
            </p>
          </div>

          {/* History Table */}
          {showPriceHistory?.priceHistory && showPriceHistory.priceHistory.length > 0 ? (
            <div>
              <p className="text-sm font-medium text-slate dark:text-mist mb-2">Previous Prices</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray dark:border-night-pond">
                    <th className="text-left py-2 text-chalkboard dark:text-rain-cloud font-medium">Price</th>
                    <th className="text-left py-2 text-chalkboard dark:text-rain-cloud font-medium">Effective Date</th>
                    <th className="text-left py-2 text-chalkboard dark:text-rain-cloud font-medium">Updated By</th>
                  </tr>
                </thead>
                <tbody>
                  {showPriceHistory.priceHistory.map((entry: PriceHistoryEntry) => (
                    <tr key={entry.id} className="border-b border-gray/50 dark:border-night-pond/50">
                      <td className="py-2 font-medium text-slate dark:text-mist">
                        {formatCurrency(entry.price)}
                      </td>
                      <td className="py-2 text-chalkboard dark:text-rain-cloud">
                        {formatDate(new Date(entry.effectiveDate))}
                      </td>
                      <td className="py-2 text-chalkboard dark:text-rain-cloud">
                        {entry.updatedBy}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-chalkboard dark:text-rain-cloud py-4">
              No price history available.
            </p>
          )}

          <div className="flex justify-end pt-2">
            <button onClick={() => setShowPriceHistory(null)} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
